import React from "react";

import { Eyebrow, MicroBadge, Lead } from "./Panel";
import { fixedDecimals } from "../../../helpers/helpers";
import { buildLineRows } from "../writes/chargeModel";
import { readReferral } from "./referralDisplay";
import { useTranslation } from "react-i18next";

/**
 * The metric strip.
 *
 * Laid out like the design — eyebrow, large tabular figure, a delta sub-line —
 * but every card reads a value the API actually returns. The design's Cost,
 * Margin, Transit, Commitment and Claimable cards are computed from fields its
 * prototype marked `_wb`, i.e. synthetic. Those collapse into one honest
 * "not on record" card: a fabricated margin is a number an admin might re-bill
 * against, which is the worst outcome on this screen.
 */

const TONES = {
  default: { border: "border-ui-border", label: "", value: "text-ui-text" },
  billed: {
    border: "border-state-info-border",
    label: "!text-state-info-text",
    value: "text-ui-text",
  },
  markup: {
    border: "border-state-purple-border",
    label: "!text-state-purple-text",
    value: "text-state-purple-text",
  },
};

const Card = ({
  label,
  badge,
  badgeTone,
  value,
  valueTone,
  sub,
  subTone = "muted",
  tone = "default",
}) => {
  const t = TONES[tone] ?? TONES.default;
  const subColour =
    subTone === "up"
      ? "text-state-success-text"
      : subTone === "down"
        ? "text-state-danger-text"
        : subTone === "markup"
          ? "text-state-purple-text"
          : "text-ui-text-muted";

  // The design colours the FIGURE, not only its caption, wherever the figure
  // itself carries the judgement — margin, variance, claimable.
  const valueColour =
    valueTone === "up"
      ? "text-state-success-text"
      : valueTone === "down"
        ? "text-state-danger-text"
        : valueTone === "markup"
          ? "text-state-purple-text"
          : t.value;

  return (
    <div className={`rounded-[7px] border bg-ui-surface px-2.5 py-2 ${t.border}`}>
      <div className="flex items-center">
        <Eyebrow className={t.label}>{label}</Eyebrow>
        {badge && <MicroBadge tone={badgeTone}>{badge}</MicroBadge>}
      </div>
      <div
        className={`mt-1 font-manrope text-[15px] font-extrabold tabular-nums ${valueColour}`}
      >
        {value}
      </div>
      {sub && (
        <div
          className={`mt-0.5 font-manrope text-[10.5px] font-semibold ${subColour}`}
        >
          {sub}
        </div>
      )}
    </div>
  );
};

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const money = (value) =>
  num(value) == null ? "—" : `$${fixedDecimals(value)}`;

const signed = (value) =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${fixedDecimals(Math.abs(value))}`;

const MetricCards = ({ data }) => {
  const { t } = useTranslation("admin");
  const rows = buildLineRows(data);
  const charges = rows.effectiveRevisions;

  // sub_total and tax live on the BASE revision; later revisions are additions
  // that carry sub_total 0 and no tax lines. Reading them off the last
  // revision shows zero on any shipment that has ever been adjusted.
  const quoteTotal = rows.quoteTotal;
  const billedTotal = rows.total || num(data?.Total);
  const serviceCharge = rows.serviceCharge;
  // After a Modify the ledger's tax lines carry what the Modify changed, so
  // the card follows them; otherwise the base revision's tax, as before.
  const taxTotal = (rows.hasModify ? rows.flatTaxTotal : rows.baseTax) || 0;
  // Display only (referralDisplay.js) — in no payload anywhere.
  const referral = readReferral(data);

  const lastAdjustment = rows.adjustments[rows.adjustments.length - 1] || null;

  const vsQuote =
    quoteTotal != null && billedTotal != null ? billedTotal - quoteTotal : null;

  return (
    <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(122px,1fr))]">
      {quoteTotal != null && num(data?.markup) > 0 && (
        <Card
          label={t("invoice.preMarkup")}
          badge="carrier-side"
          value={money(quoteTotal)}
          sub="the pre-markup row stored at booking"
        />
      )}

      <Card
        label={t("invoice.billed")}
        tone="billed"
        value={money(billedTotal)}
        sub={
          vsQuote != null && num(data?.markup) > 0
            ? t("invoice.vsPreMarkup", { amount: signed(vsQuote) })
            : t("invoice.revisionCount", { count: charges.length })
        }
        subTone={
          vsQuote == null || !num(data?.markup)
            ? "muted"
            : vsQuote > 0
              ? "up"
              : vsQuote < 0
                ? "down"
                : "muted"
        }
      />

      <Card
        label={t("invoice.subTotalCard")}
        badge={t("invoice.inclMarkup")}
        badgeTone="purple"
        value={money(rows.baseSubTotal)}
        sub={t("invoice.beforeTax")}
      />

      {serviceCharge > 0 && (
        <Card
          label={t("invoice.serviceCharge")}
          badge={data?.markup > 0 ? `${parseFloat(data.markup)}%` : null}
          badgeTone="purple"
          tone="markup"
          valueTone="markup"
          value={money(serviceCharge)}
          sub={
            data?.markup > 0
              ? t("invoice.markupRate", { rate: parseFloat(data.markup) })
              : t("invoice.markupNotSet")
          }
          subTone="markup"
        />
      )}

      {/* Referral markup — shipments.referral_markup, a server field like the
          service charge. Display only; in no payload anywhere. */}
      {referral.amount > 0 && (
        <Card
          label={t("invoice.referralMarkup")}
          badge={referral.rate}
          badgeTone="purple"
          tone="markup"
          valueTone="markup"
          value={money(referral.amount)}
          sub={referral.detail}
          subTone="markup"
        />
      )}

      <Card
        label={t("invoice.taxCard")}
        value={taxTotal > 0 ? money(taxTotal) : "—"}
        sub={
          rows.baseTaxes.length
            ? rows.baseTaxes
                .map((tax) => tax.label)
                .filter(Boolean)
                .join(" · ")
            : t("invoice.noTaxLines")
        }
      />

      <Card
        label={t("invoice.addedAfterBookingCard")}
        badge={rows.adjustments.length ? "post-booking" : null}
        badgeTone="warning"
        valueTone={
          !rows.adjustments.length
            ? undefined
            : rows.adjustmentsTotal > 0
              ? "up"
              : "down"
        }
        value={
          rows.adjustments.length ? signed(rows.adjustmentsTotal) : "—"
        }
        sub={
          rows.adjustments.length
            ? lastAdjustment
              ? t("invoice.adjustmentsLast", {
                  count: rows.adjustments.length,
                  amount: signed(lastAdjustment.amount),
                })
              : t("invoice.adjustments", { count: rows.adjustments.length })
            : t("invoice.nothingAddedSinceBooking")
        }
        subTone={
          !rows.adjustments.length
            ? "muted"
            : rows.adjustmentsTotal > 0
              ? "up"
              : "down"
        }
      />

      <Card
        label={t("invoice.shipment")}
        value={t("invoice.packagesShort", {
          packages: data?.shipment_details?.length ?? 0,
        })}
        sub={`${data?.total_weight ?? 0} ${
          data?.unit === "Imperial" ? "lb" : "kg"
        }`}
      />

      {/* Cost and margin are REAL once the carrier billing feed has posted an
          invoice against this shipment. Until then they are absent rather than
          inferred — a fabricated margin is a number an admin might re-bill
          against, which is the worst outcome on this screen. */}
      {rows.hasCarrierBill ? (
        <>
          <Card
            label={t("invoice.carrierCost")}
            badge={rows.carrierInvoiceNumbers[0] || t("invoice.billedBadge")}
            value={money(rows.carrierTotal)}
            sub={t("invoice.fromTheCarriersOwnInvoice")}
          />
          <Card
            label={t("invoice.margin")}
            value={money(rows.margin)}
            valueTone={rows.margin > 0 ? "up" : rows.margin < 0 ? "down" : undefined}
            sub={
              billedTotal
                ? t("invoice.marginOfBilled", { pct: Math.round((rows.margin / billedTotal) * 100) })
                : t("invoice.billedMinusCost")
            }
            subTone={rows.margin > 0 ? "up" : rows.margin < 0 ? "down" : "muted"}
          />
        </>
      ) : (
        <div className="rounded-[7px] border border-dashed border-ui-border-strong bg-ui-surface px-2.5 py-2">
          <div className="flex items-center">
            <Eyebrow>{t("invoice.costMargin")}</Eyebrow>
            <MicroBadge>{t("invoice.awaitingBill")}</MicroBadge>
          </div>
          <div className="mt-1 font-manrope text-[11.5px] font-medium leading-snug text-ui-text-muted">
            <Lead label={t("invoice.noCarrierBillYet")}>
              {t("invoice.costMarginAppear")}
            </Lead>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricCards;
